import {
  Component, ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {ListViewFilterFunction} from '../../../modules/admin/components/list-view.component';
import {PAGINATION_DEFAULT_PAGE_SIZE, PaginationComponent} from './pagination.component';
import {AnimationOptions} from 'ngx-lottie';

export type PaginationCallbackFunction<T = any> = (pageSize: number, page: number) => void;

@Component({
  selector: 'uruk-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class ListComponent implements OnInit, OnDestroy, OnChanges {

  // items in the list
  @Input() list: any[] = [];
  @ViewChild('selectAllCheckbox') selectAllCheckbox: ElementRef<HTMLInputElement>;
  @ViewChild('pagination') paginationComponent: PaginationComponent;
  // function to set the visibility of delete button for the item
  @Input() displayDeleteButton: ListViewFilterFunction = (item: any) => true;
  @Input() displayActionButton: boolean;
  @Input() displayEditButton: boolean = true;
  @Input() displayCount: boolean = true;
  @Input() displaySearch: boolean = false;
  @Input() tableDataLoading: boolean = false;
  @Input() displayFilter: boolean = false;
  @Input() displayMultipleActions: boolean = false;
  @Input() displayVersionUpdateActions: boolean = false;
  @Input() isMultipleActionsDisable: boolean = true;
  @Input() displayCheckBox: boolean = false;
  @Input() columnMetadata: any[];
  /**
   * Keeps the metadata of statuses displayed at the top of component.
   * Each object in metadata should have the following format:
   * {
   *   count: number; the status count displayed next to title
   *   title: string; the status title
   *   class: string; a css class to be applied to the title
   *   commaSeparated: boolean; whether a comma should be appended to the status text
   * }
   * */
  @Input() statusMetadata: any[];
  // type of entries displayed in the list component
  @Input() type: string;
  @Input() searchPlaceholder: string = '';
  @Input() deleteIconClass: string = 'fa fa-trash';
  @Input() editIconClass: string = 'fa fa-eye';
  @Input() actionIconClass: string = 'fa fa-arrow-right';
  // callback function to be called when entries are paginated
  @Input() paginationCallback: any;
  @Output() onEditItem: EventEmitter<any> = new EventEmitter<any>();
  @Output() onDeleteItem: EventEmitter<string> = new EventEmitter<string>();
  @Output() onActionItem: EventEmitter<any> = new EventEmitter<any>();
  @Output() onSelectItem: EventEmitter<string> = new EventEmitter<string>();
  @Output() onSelectAllItem: EventEmitter<any> = new EventEmitter<any>();
  @Output() onDeleteItems: EventEmitter<any> = new EventEmitter<any>();
  @Output() onUploadNewVersion: EventEmitter<any> = new EventEmitter<any>();
  @Output() onUpdateModuleVersions: EventEmitter<any> = new EventEmitter<any>();
  @Output() onSelectColumns: EventEmitter<any> = new EventEmitter<any>();
  @Output() onUpdateItems: EventEmitter<any> = new EventEmitter<any>();
  @Output() onFilterItems: EventEmitter<any> = new EventEmitter<any>();
  @Output() onResetItems: EventEmitter<any> = new EventEmitter<any>();
  @Output() onResetAllSelections: EventEmitter<any> = new EventEmitter<any>();
  @Output() onSortAscending: EventEmitter<string> = new EventEmitter<string>();
  @Output() onSortDescending: EventEmitter<string> = new EventEmitter<string>();
  @Output() onTextSearch: EventEmitter<string> = new EventEmitter<string>();

  // search text typed by the user
  searchText: string;
  // keeps the visibility of delete buttons for items
  deleteButtonVisibilityForItem: boolean[];
  // page size of pagination
  public pageSize: number = PAGINATION_DEFAULT_PAGE_SIZE;
  isInitialized: boolean;

  currentSortField: string = 'id';
  sortDirection: string = 'asc';
  selectedFields: string[] = [];
  private readonly MAX_GRID = 11;
  private prevSelectedFields: string[] = [];

  private colWidth = (field: string) =>
    (this.columnMetadata?.find(c => c.field === field)?.columnWidth) || 0

  private totalWidth = (fields: string[]) =>
    fields.reduce((s, f) => s + this.colWidth(f), 0)
  lottieOptions: AnimationOptions = {
    path: '/assets/animation/uruk-5sec.json',
  };

  constructor() {

  }

  ngOnInit(): void {
    this.isInitialized = true;
    this.syncSelectedFieldsFromMetadata();
    this.prevSelectedFields = [...this.selectedFields];
  }

  ngOnDestroy(): void {

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.columnMetadata && changes.columnMetadata.currentValue !== changes.columnMetadata.previousValue) {
      this.syncSelectedFieldsFromMetadata();
    }
    if ((changes.list && changes.list.currentValue !== changes.list.previousValue) || (changes.displayDeleteButton && changes.displayDeleteButton.currentValue !== changes.displayDeleteButton.previousValue)) {
      this.setDeleteButtonVisibilityForItems();
      if (this.isInitialized && this.displayCheckBox) {
        this.selectAllCheckbox.nativeElement.checked = false;
        this.isMultipleActionsDisable = true;
        this.onResetAllSelections.emit();
      }
    }
  }

  /**
   * Handles the pagination event.
   * @param event the pagination event
   */
  public paginate(event) {
    this.pageSize = event.pageSize;
    this.paginationCallback(event.pageSize, event.page);
  }

  public onReset() {
    if (this.paginationComponent) {
      this.paginationComponent.resetPagination();
    }
  }

  /**
   * Populates {@link deleteButtonVisibilityForItem} array by applying {@link displayDeleteButton} function to each item
   * in the list.
   * */
  private setDeleteButtonVisibilityForItems() {
    if (this.list) {
      this.deleteButtonVisibilityForItem = this.list.map(item => this.displayDeleteButton(item));
    }
  }

  handleTextSearch() {
    this.onTextSearch.emit(this.searchText);
  }

  public sortAscending(field: string) {
    this.onSortAscending.emit(field);
    this.currentSortField = field;
    this.sortDirection = 'asc';
    this.onReset();
  }

  public sortDescending(field: string) {
    this.onSortDescending.emit(field);
    this.currentSortField = field;
    this.sortDirection = 'desc';
    this.onReset();
  }

  handleSelectAll() {
    const checkbox = this.selectAllCheckbox.nativeElement;
    const isChecked = checkbox.checked;

    this.onSelectAllItem.emit(isChecked);
    this.selectAll(isChecked);
  }


  selectAll(isChecked: boolean) {
    const checkboxes: NodeListOf<HTMLInputElement> = document.querySelectorAll('.actions-check-input');

    // Check if the checkbox is checked or not
    checkboxes.forEach(checkbox => {
      checkbox.checked = isChecked;
    });
  }
  get visibleColumns() {
    return (this.columnMetadata || []).filter(c => c.show !== false);
  }

  trackByField(_: number, col: any) { return col.field; }

  private syncSelectedFieldsFromMetadata() {
    this.selectedFields = (this.columnMetadata || [])
      .filter(c => c.show !== false)
      .map(c => c.field);

    this.selectedFields = [...this.selectedFields];
  }

  onColumnsChange(nextSelected: string[]) {
    const keep = this.prevSelectedFields.filter(f => nextSelected.includes(f));
    let total = this.totalWidth(keep);

    const added = nextSelected.filter(f => !this.prevSelectedFields.includes(f));

    for (const f of added) {
      const w = this.colWidth(f);
      if (total + w <= this.MAX_GRID) {
        keep.push(f);
        total += w;
      }
    }
    this.selectedFields = [...keep];

    this.columnMetadata = (this.columnMetadata || []).map(c => ({
      ...c,
      show: this.selectedFields.includes(c.field),
    }));

    this.prevSelectedFields = [...this.selectedFields];

    this.onSelectColumns.emit([...this.columnMetadata]);
  }

  get remainingColumnWidth(): number {
    const total = this.totalWidth(this.visibleColumns.map(c => c.field));
    return Math.max(0, this.MAX_GRID - total);
  }

  canSelect(col: any): boolean {
    const current = this.totalWidth(this.selectedFields);
    const thisWidth = col.columnWidth || 0;
    return current + thisWidth <= this.MAX_GRID || this.selectedFields.includes(col.field);
  }


}
